// CadastreInfoComponent.js
import { useState, useEffect, useRef } from 'react';
import { EditableInputComponent, EditableSelectComponent, EditableTextareaComponent, SectionHeader } from "components";
import { Document } from "assets/icons"; 
import { toast } from "react-toastify";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { FileLabel } from 'shared/types/file';
import ModalUploadFileLog, { FileUploadField } from 'shared/components/uploadFileModal/uploadFileModal';
import { BuildingDTO } from 'shared/types/building';
import { UnitDTO } from 'shared/types/unit';
import { UnitConformityInfo, UnitConformityStatus } from 'shared/types/unit-conformity-info.dto';
import FileLogTable from 'shared/components/uploadFileModal/fileLogTable';

interface ConformityInfoComponentProps {
    unitConformityInfo: UnitConformityInfo;
    handleUnitConformityInfoChangeInParent?: (value: any) => any;
    handleSaveInParent?: () => any;
    handleCloseParent?: () => any;
    building: BuildingDTO,
    unit: UnitDTO,
    parentEditMode?: boolean;
}

const ConformityInfoComponent = ({
    unitConformityInfo,
    handleCloseParent,
    handleSaveInParent,
    building,
    unit,
    handleUnitConformityInfoChangeInParent,
    parentEditMode = true,
}: ConformityInfoComponentProps) => {
    const [editMode, setEditMode] = useState(parentEditMode);
    const [editedUnitConformityInfo, setEditedUnitConformityInfo] = useState(unitConformityInfo);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { session } = useSession();
    const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
    const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(null);
    const fileLogTableRef = useRef<{ saveChanges: () => Promise<void>; hasUnsavedChanges: boolean } | null>(null);

    const conformityFileLabels = [
        FileLabel.ConformiteitsattestUnit
        // Add any other conformity-related file labels here
    ];

    useEffect(() => {
        console.log(unitConformityInfo);
        setEditedUnitConformityInfo(unitConformityInfo);
    }, []); // don't use the dependency here because it will refresh the component on tab switch


    const handleSave = async () => {
        setHasUnsavedChanges(false);
        try {
            // First save the file log changes if there are any
            if (fileLogTableRef.current?.hasUnsavedChanges) {
                await fileLogTableRef.current.saveChanges();
            }

            if (handleSaveInParent) {
                handleSaveInParent();
            } else {
                await fetchWithAuth(
                    `${API_URL}/units/conformity-info`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(editedUnitConformityInfo),
                    },
                    session,
                );
                toast.success("Conformiteitsinformatie van unit succesvol bijgewerkt!");
            }
        } catch (e) {
            console.error(e);
            toast.error("Er is een fout opgetreden bij het bijwerken van de conformiteitsinformatie.");
        }
    };

    const handleFileModalClose = async () => {
        setSelectedFileLabel(null);
        setFileModalOpen(false);
    };

    const handleFileUploadSuccess = async (fileLogData: any) => {
        const { fileLog } = fileLogData;

        let updatedField = {};

        switch (selectedFileLabel) {
            case FileLabel.ConformiteitsattestUnit:
                updatedField = {
                    unitConformityAttestFileLogId: fileLog.id,
                    unitConformityAttestFileLog: fileLog,
                };
                break;
            default:
                return;
        }

        const updatedConformityInfo = { ...unitConformityInfo, ...updatedField };

        // Save the updated information to the backend
        try {
            const response = await fetchWithAuth(
                `${API_URL}/units/conformity-info`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updatedConformityInfo),
                },
                session,
            );

            if (response.ok) {
                toast.success("Conformiteits informatie succesvol geüpdatet");
                setHasUnsavedChanges(false); // Reset unsaved changes state
                setEditedUnitConformityInfo(updatedConformityInfo); // Update local state
            } else {
                throw new Error("Failed to update unit conformity information");
            }
        } catch (error) {
            console.error("Error updating unit conformity information:", error);
            toast.error("Failed to update unit conformity information");
        }
    };

    const getInputFieldStatusColor = (value: any) => {
        return value ? "bg-green-500" : "bg-red-500"; // Green for filled, Red for empty
    };

    const updateConformityInfo = (updatedFields: any) => {
        setHasUnsavedChanges(true);
        setEditedUnitConformityInfo((prev: any) => ({ ...prev, ...updatedFields }));

        if (handleUnitConformityInfoChangeInParent) {
            handleUnitConformityInfoChangeInParent({
                ...unitConformityInfo,
                ...updatedFields,
            });
        }
    };

    const getStatusColor = (status: any) => {
        switch (status) {
            case UnitConformityStatus.OK:
            case UnitConformityStatus.NOT_REQUIRED:
                return "bg-green-500"; // Green for OK
            case UnitConformityStatus.PARTIAL_OK:
                return "bg-orange-500"; // Orange for Partial
            case UnitConformityStatus.NOT_OK:
                return "bg-red-500"; // Red for Not OK
            default:
                return "bg-transparent-500";
        }
    };


    return (
        <section className="flex flex-col gap-0 rounded-md bg-white">
            <SectionHeader
                title="Unit conformiteitsinformatie"
                icon={<Document />}
                editMode={editMode}
                sticky={false}
                onEditClick={() => setEditMode(!editMode)}
                onSaveClick={handleSave}
                showEditToggle={true}
                hasUnsavedChanges={hasUnsavedChanges || fileLogTableRef.current?.hasUnsavedChanges}
                onCloseClick={() => { if (handleCloseParent) handleCloseParent() }}
            />
            <div className="p-3">
                <div className="gap-3 sm:grid sm:grid-cols-2">
                    {/* Conformity documents */}
                    <div className="col-span-2 flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <p>Conformiteitsdocumenten</p>
                        </header>
                        <FileLogTable
                            ref={fileLogTableRef}
                            acceptedLabels={conformityFileLabels}
                            onUploadClick={() => setFileModalOpen(true)}
                            building={building || undefined}
                            unit={unit || undefined}
                        />
                    </div>
                    
                    {/* Conformity score */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getInputFieldStatusColor(editedUnitConformityInfo?.unitMaxCapacityPersons)}`} />
                            <p>Hoeveel personen kunnen deze unit maximaal bewonen?</p>
                        </header>
                        <EditableInputComponent
                            label="Maximale bewooncapaciteit"
                            value={editedUnitConformityInfo?.unitMaxCapacityPersons as any || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateConformityInfo({ unitMaxCapacityPersons: value })}
                        />
                    </div>

                    {/* Conformity status */}
                    <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                                editedUnitConformityInfo && editedUnitConformityInfo?.unitConformity,
                            )}`} />
                            <p>Is de unit conform?</p>
                        </header>
                        <EditableSelectComponent
                            label="Maak een keuze"
                            value={editedUnitConformityInfo && editedUnitConformityInfo?.unitConformity || ""}
                            isLoading={false}
                            options={Object.values(UnitConformityStatus).map(value => ({ label: value, value }))}
                            editMode={editMode}
                            onChange={(value) => updateConformityInfo({ unitConformity: value })}
                        />
                    </div>

                    {/* Conformity score */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getInputFieldStatusColor(editedUnitConformityInfo?.unitConformityScore)}`} />
                            <p>Conformiteitsscore</p>
                        </header>
                        <EditableInputComponent
                            label="Conformiteitsscore"
                            value={editedUnitConformityInfo?.unitConformityScore as any || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateConformityInfo({ unitConformityScore: value })}
                        />
                    </div>


                    {/* Building Permit Remarks */}
                    <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                            <p>Opmerkingen op de conformiteitsstatus</p>
                        </header>
                        <EditableTextareaComponent
                            label="Opmerkingen"
                            value={editedUnitConformityInfo && editedUnitConformityInfo?.unitConformityRemarks || ""}
                            isLoading={false}
                            editMode={editMode}
                            onChange={(value) => updateConformityInfo({ unitConformityRemarks: value })}
                        />
                    </div>

                    {/* Modal for File Upload */}
                    <ModalUploadFileLog
                        isOpen={fileModalOpen}
                        onClose={handleFileModalClose}
                        unit={unit}
                        building={building}
                        skipFields={[FileUploadField.BUILDING]}
                        customLabels={conformityFileLabels}
                        onFileUploadSuccess={handleFileUploadSuccess}
                    />
                </div>
            </div>
        </section>
    );
};

export default ConformityInfoComponent;
